import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { CommercialClient } from "../../models/CommercialClient";
import { companyTypeLabel } from "../../models/Company";
import { IntranetImportPreview, IntranetImportPreviewFicha, IntranetImportResult } from "../../models/Intranet";
import { previewNotionImport, runNotionImport } from "../../services/intranetService";
import { crm } from "../../theme";

const NEW = "new";
const SKIP = "skip";
// Sugestão forte entra marcada; fraca entra marcada mas pede conferência
const STRONG = 80;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clients: CommercialClient[];
  onImported: () => void;
}

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;

const initialTarget = (ficha: IntranetImportPreviewFicha) => ficha.candidates[0]?.commercialClientId ?? NEW;

const NotionImportModal = ({ isOpen, onClose, clients, onImported }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<IntranetImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<"all" | "check" | "new">("all");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<IntranetImportResult | null>(null);
  // Sugestão fraca só vale depois que alguém olhou: trocar a opção ou "Aceitar sugestões"
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const toast = useToast();

  const sortedClients = useMemo(() => [...clients].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")), [clients]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    setFilter("all");
    setConfirmed(new Set());
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const loadPreview = async (selected: File) => {
    setFile(selected);
    setBusy(true);
    try {
      const { data } = await previewNotionImport(selected);
      if (data?.success === false) {
        toast({ title: data.notifications?.[0] ?? "Pacote inválido.", status: "error" });
        setFile(null);
        return;
      }
      const loaded = data as IntranetImportPreview;
      setPreview(loaded);
      setMapping(Object.fromEntries(loaded.fichas.map((ficha) => [ficha.key, initialTarget(ficha)])));
    } catch {
      toast({ title: "Não foi possível ler o pacote.", status: "error" });
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await runNotionImport(file, mapping);
      setResult(data);
      if (data?.success) onImported();
      else toast({ title: data?.notifications?.[0] ?? "Importação falhou.", status: "error" });
    } catch {
      toast({ title: "Importação falhou.", status: "error" });
    } finally {
      setBusy(false);
    }
  };

  const needsCheck = (ficha: IntranetImportPreviewFicha) => {
    const target = mapping[ficha.key];
    const candidate = ficha.candidates.find((c) => c.commercialClientId === target);
    return candidate != null && candidate.score < STRONG && !confirmed.has(ficha.key);
  };

  const rows = (preview?.fichas ?? []).filter((ficha) =>
    filter === "all" ? true : filter === "check" ? needsCheck(ficha) : mapping[ficha.key] === NEW);

  const counts = preview && {
    check: preview.fichas.filter(needsCheck).length,
    create: preview.fichas.filter((f) => mapping[f.key] === NEW).length,
    skip: preview.fichas.filter((f) => mapping[f.key] === SKIP).length,
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="6xl" scrollBehavior="inside" closeOnOverlayClick={!busy}>
      <ModalOverlay />
      <ModalContent mx="16px">
        <ModalHeader>Importar do Notion</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {!preview && !result && (
            <Box>
              <Text fontSize="sm" color={crm.textMuted} mb="12px">
                Envie o pacote .zip gerado na extração do Notion. Primeiro mostramos o cliente sugerido para cada ficha; nada é gravado antes de confirmar.
                Reimportar é seguro: procedimento já importado não entra de novo e campo já preenchido no sistema não é sobrescrito.
              </Text>
              <Input type="file" accept=".zip" p="4px" isDisabled={busy} onChange={(e) => e.target.files?.[0] && loadPreview(e.target.files[0])} />
              {busy && <Text mt="10px" fontSize="sm">Lendo pacote...</Text>}
            </Box>
          )}

          {preview && !result && counts && (
            <Box>
              <Flex gap="16px" wrap="wrap" fontSize="sm" mb="12px" color={crm.textMuted}>
                <Text><b>{preview.fichas.length}</b> fichas</Text>
                <Text><b>{preview.generalCount}</b> procedimentos gerais{preview.generalAlreadyImported ? ` (${preview.generalAlreadyImported} já importados)` : ""}</Text>
                <Text><b>{preview.fileCount}</b> arquivos ({megabytes(preview.totalBytes)})</Text>
                <Text color={crm.warning}><b>{counts.check}</b> p/ conferir</Text>
                <Text><b>{counts.create}</b> clientes novos</Text>
                <Text><b>{counts.skip}</b> ignorados</Text>
              </Flex>
              <Flex gap="6px" mb="10px">
                {([["all", "Todas"], ["check", "Conferir"], ["new", "Clientes novos"]] as const).map(([value, label]) => (
                  <Button key={value} size="xs" variant={filter === value ? "solid" : "outline"} colorScheme={filter === value ? "blue" : "gray"} onClick={() => setFilter(value)}>{label}</Button>
                ))}
                {counts.check > 0 && <Button size="xs" colorScheme="orange" variant="outline" ml="auto" onClick={() => setConfirmed(new Set(preview.fichas.map((f) => f.key)))}>Aceitar sugestões a conferir</Button>}
              </Flex>
              <Box border="1px solid" borderColor={crm.border} borderRadius={crm.radius} overflowX="auto">
                <Box as="table" w="100%" minW="760px" sx={{ borderCollapse: "collapse", "th, td": { borderBottom: "1px solid", borderColor: crm.border, px: "10px", py: "7px", textAlign: "left", fontSize: "sm", verticalAlign: "middle" }, th: { bg: crm.subtle, color: crm.textMuted, fontWeight: 600 } }}>
                  <thead><tr><th>Ficha no Notion</th><th>Empresa</th><th>Conteúdo</th><th style={{ width: "45%" }}>Importar em</th></tr></thead>
                  <tbody>
                    {rows.map((ficha) => {
                      const target = mapping[ficha.key];
                      const others = sortedClients.filter((c) => !ficha.candidates.some((cand) => cand.commercialClientId === c.id));
                      return (
                        <tr key={ficha.key} style={needsCheck(ficha) ? { background: "#fffbeb" } : undefined}>
                          <td><Text fontWeight="600">{ficha.name}</Text></td>
                          <td>{companyTypeLabel(ficha.unit)}</td>
                          <td>
                            <Text fontSize="xs" color={crm.textMuted}>{ficha.procedures} proc. · {ficha.files} arq.</Text>
                            {ficha.alreadyImported > 0 && <Badge colorScheme="green" fontSize="2xs">{ficha.alreadyImported} já importados</Badge>}
                          </td>
                          <td>
                            <Select size="sm" value={target} onChange={(e) => { const value = e.target.value; setMapping((current) => ({ ...current, [ficha.key]: value })); setConfirmed((current) => new Set(current).add(ficha.key)); }} borderColor={needsCheck(ficha) ? crm.warning : crm.borderStrong}>
                              {ficha.candidates.length > 0 && (
                                <optgroup label="Sugestões">
                                  {ficha.candidates.map((c) => <option key={c.commercialClientId} value={c.commercialClientId}>{c.name} ({c.score}%)</option>)}
                                </optgroup>
                              )}
                              <optgroup label="Ações">
                                <option value={NEW}>Criar novo cliente "{ficha.name}"</option>
                                <option value={SKIP}>Ignorar esta ficha</option>
                              </optgroup>
                              <optgroup label="Outro cliente">
                                {others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </optgroup>
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Box>
                {rows.length === 0 && <Text p="16px" fontSize="sm" color={crm.textMuted}>Nada neste filtro.</Text>}
              </Box>
            </Box>
          )}

          {result && (
            <Box fontSize="sm">
              <Text fontWeight="700" color={result.success ? crm.success : crm.danger} mb="8px">{result.success ? "Importação concluída." : "Importação falhou."}</Text>
              <Text>Fichas atualizadas: <b>{result.clientsUpdated}</b> · ignoradas: <b>{result.fichasSkipped}</b></Text>
              <Text>Procedimentos criados: <b>{result.proceduresCreated}</b> · já existentes/vazios: <b>{result.proceduresSkipped}</b></Text>
              <Text>Arquivos salvos: <b>{result.filesSaved}</b></Text>
              {result.clientsCreated.length > 0 && (
                <Box mt="10px"><Text fontWeight="600">Clientes criados ({result.clientsCreated.length}) — revise no Clientes Ativos:</Text><Text color={crm.textMuted}>{result.clientsCreated.join(", ")}</Text></Box>
              )}
              {result.notifications.length > 0 && (
                <Box mt="10px"><Text fontWeight="600">Avisos:</Text>{result.notifications.map((n, i) => <Text key={i} color={crm.textMuted}>• {n}</Text>)}</Box>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          {preview && !result && <Button variant="ghost" onClick={reset} isDisabled={busy}>Trocar arquivo</Button>}
          {preview && !result && <Button colorScheme="blue" onClick={runImport} isLoading={busy} loadingText="Importando" isDisabled={(counts?.check ?? 0) > 0} title={(counts?.check ?? 0) > 0 ? "Confira as sugestões em amarelo" : undefined}>Importar</Button>}
          {(result || !preview) && <Button onClick={close} isDisabled={busy}>Fechar</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NotionImportModal;
